
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Bell, Calendar, Settings, User, MessageSquare, Plus } from "lucide-react";
import { getRoleAvatarData } from "@/lib/avatarUtils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NewAnnouncementDialog from "./announcements/NewAnnouncementDialog";

const Header = () => {
  const { user, logout } = useAuth();
  const [isAnnouncementOpen, setIsAnnouncementOpen] = useState(false);
  
  const isAdmin = user?.role === "board" || user?.role === "teacher";

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case "parent":
        return "bg-secondary text-secondary-foreground";
      case "teacher":
        return "bg-accent text-accent-foreground";
      case "board":
        return "bg-primary text-primary-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <header className="border-b bg-background px-4 py-3 flex items-center justify-between">
      <div className="md:w-64">
        <h1 className="text-lg font-semibold hidden md:block">
          Parent-cooperative preschool
        </h1>
      </div>

      <div className="flex items-center space-x-2">
        {isAdmin && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="flex items-center gap-1"
            onClick={() => setIsAnnouncementOpen(true)}
          >
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Announcement</span>
          </Button>
        )}
        <Button variant="ghost" size="icon">
          <Bell className="h-5 w-5" />
        </Button>
        <Button variant="ghost" size="icon">
          <Calendar className="h-5 w-5" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-10 w-10 rounded-full">
              <Avatar>
                <AvatarFallback 
                  className={user ? getRoleAvatarData(user.role, user.name, user.email).backgroundColor : ""}
                >
                  {user ? getInitials(user.name) : "U"}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user?.name}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email}
                </p>
                <span
                  className={`mt-1 text-xs px-2 py-0.5 rounded-full w-fit capitalize ${getRoleBadgeClass(
                    user?.role || ""
                  )}`}
                >
                  {user?.role}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <User className="mr-2 h-4 w-4" />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Settings className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>Log out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <NewAnnouncementDialog 
        isOpen={isAnnouncementOpen} 
        onClose={() => setIsAnnouncementOpen(false)}
      />
    </header>
  );
};

export default Header;
